<template>
  <div class="flex w-full flex-col">
    <button
      class="mb-4 mr-auto flex w-auto items-center gap-2 rounded-full border border-gray-300 px-2 py-1 text-xs text-gray-500 hover:border-gray-500 hover:text-gray-700 md:text-sm"
      @click="emit('close')"
    >
      <ChevronLeftIcon class="size-4" />
      <span>Volver a cotizaciones</span>
    </button>
    <div class="relative mt-0 h-1 w-full rounded-full bg-gray-100">
      <div
        class="absolute left-0 top-0 h-1 rounded-full bg-gray-700 transition-all duration-300"
        :style="{ width: `${progressPercent}%` }"
      ></div>
    </div>
    <div class="mt-2 flex items-center justify-between">
      <button
        v-for="step in steps"
        :key="step"
        class="flex flex-col items-center hover:underline"
        @click="updateStep(step)"
      >
        <span class="text-xs text-gray-500">{{ stepTranslation(step) }}</span>
      </button>
    </div>
    <div class="mt-5 flex w-full flex-col md:mt-8">
      <maker-pre-order-new
        v-if="currentStep === 'create'"
        :maker="maker"
        :pre-order="selectedPreOrder"
        @pre-order:created="handlePreOrderCreated"
        @pre-order:updated="handlePreOrderUpdated"
        @move-to:add-products="(currentStep = 'add-products')"
      />
      <maker-pre-order-new-products
        v-else-if="currentStep === 'add-products' && selectedPreOrder?.id && !preOrderProductsIsLoading"
        :maker="maker"
        :pre-order="selectedPreOrder"
        :pre-order-products="preOrderProducts"
        @close="emit('close')"
        @move-to:share="(currentStep = 'share')"
        @move-to:create-update="(currentStep = 'create')"
      />
      <maker-pre-order-share
        v-else-if="currentStep === 'share' && selectedPreOrder?.id"
        :maker="maker"
        :pre-order="selectedPreOrder"
        @move-to:create-update="(currentStep = 'create')"
        @close="emit('close')"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue';
import { ChevronLeftIcon } from '@heroicons/vue/24/outline';
import { useQuery } from 'vue-query';
import preOrderProductsApi from 'api/pre-order-products';
import makerPreOrderNew from './maker-pre-order-new.vue';
import makerPreOrderNewProducts from './maker-pre-order-new-products.vue';
import makerPreOrderShare from './maker-pre-order-share.vue';

const props = defineProps<{
  maker: Maker;
  preOrder?: PreOrder;
}>();

const emit = defineEmits<{
  close: [];
}>();

const steps = ref(['create', 'add-products', 'share']);

const progressPercent = computed(() => {
  if (currentStep.value === 'create') return 0;
  if (currentStep.value === 'add-products') return 50;
  if (currentStep.value === 'share') return 100;
  return 0;
});

function stepTranslation(step: string) {
  if (step === 'create') return 'Crear';
  if (step === 'add-products') return 'Agregar productos';
  if (step === 'share') return 'Compartir';
  return '';
}

const currentStep = ref('create');
const selectedPreOrder = ref(props.preOrder);

function updateStep(step: string) {
  if (selectedPreOrder.value?.id) {
    currentStep.value = step;
  }
}

const handlePreOrderCreated = (preOrder: PreOrder) => {
  selectedPreOrder.value = preOrder;
  currentStep.value = 'add-products';
};

const handlePreOrderUpdated = (preOrder: PreOrder) => {
  selectedPreOrder.value = preOrder;
  currentStep.value = 'add-products';
};

const computedPreOrderId = computed(() => {
  return selectedPreOrder.value?.id || 0;
});

const preOrderProductsQuery = useQuery(
  ['preOrderProducts', computedPreOrderId],
  () => preOrderProductsApi.index(computedPreOrderId.value),
  {
    enabled: computed(() => !!computedPreOrderId.value),
  },
);
const preOrderProductsIsLoading = computed(() => preOrderProductsQuery.isLoading.value);
const preOrderProducts = computed(() => preOrderProductsQuery.data.value?.data?.preOrderProducts as PreOrderProduct[]);
</script>
